/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, StudioCampaign } from "../types";
import { STUB_PRODUCTS } from "../stub";
import { Field, formatShortDate, Segmented } from "../shared";
import { sampleCouponCode } from "../variables";
import { CouponCard } from "../CouponCard";

type OfferStepProps = {
  campaign: StudioCampaign;
  onChange: (patch: Partial<StudioCampaign>) => void;
};

type Coupon = Extract<CampaignOffer, { type: "coupon" }>;

const DEFAULT_COUPON: Coupon = {
  type: "coupon",
  reward: "percentage",
  value: 10,
  product_id: null,
  min_spend: null,
  expires_in_days: 14,
  single_use: true,
};

export const OfferStep = defineComponent<OfferStepProps>({
  name: "CampaignOfferStep",
  props: ["campaign", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const patch = (p: Partial<Coupon>) => {
      const current = props.campaign.offer.type === "coupon" ? props.campaign.offer : DEFAULT_COUPON;
      props.onChange({ offer: { ...current, ...p } });
    };

    const numberInput = (value: number | null, onChange: (v: number | null) => void, suffix: string, step = 1) => (
      <label class="input input-bordered input-sm flex items-center gap-2">
        <input
          type="number"
          class="w-full"
          min={0}
          step={step}
          value={value ?? ""}
          onInput={(e) => {
            const raw = (e.target as HTMLInputElement).value;
            onChange(raw === "" ? null : Number(raw));
          }}
        />
        <span class="text-base-content/50">{suffix}</span>
      </label>
    );

    return () => {
      const offer = props.campaign.offer;
      const coupon = offer.type === "coupon" ? offer : null;
      return (
        <div class="mx-auto grid max-w-5xl gap-8 p-6 lg:grid-cols-[1fr_340px]">
          <div class="space-y-6">
            <Segmented
              value={offer.type}
              options={[
                { value: "coupon", label: t("campaigns.studio_offer.with_coupon"), icon: "confirmation_number" },
                { value: "none", label: t("campaigns.studio_offer.message_only"), icon: "chat_bubble" },
              ]}
              onChange={(v) => props.onChange({ offer: v === "none" ? { type: "none" } : DEFAULT_COUPON })}
            />

            {coupon ? (
              <div class="space-y-4">
                <Field label={t("campaigns.studio_offer.reward")}>
                  <Segmented
                    value={coupon.reward}
                    options={[
                      { value: "percentage", label: t("campaigns.studio_offer.reward_percentage"), icon: "percent" },
                      { value: "fixed", label: t("campaigns.studio_offer.reward_fixed"), icon: "payments" },
                      { value: "free_item", label: t("campaigns.studio_offer.reward_free_item"), icon: "redeem" },
                    ]}
                    onChange={(v) =>
                      patch({
                        reward: v as Coupon["reward"],
                        product_id: v === "free_item" ? (coupon.product_id ?? STUB_PRODUCTS[0].id) : null,
                      })
                    }
                  />
                </Field>

                <div class="grid gap-4 sm:grid-cols-2">
                  {coupon.reward === "free_item" ? (
                    <Field label={t("campaigns.studio_offer.product")}>
                      <select
                        class="select select-bordered select-sm w-full"
                        value={coupon.product_id ?? ""}
                        onChange={(e) => patch({ product_id: (e.target as HTMLSelectElement).value })}
                      >
                        {STUB_PRODUCTS.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label={t("campaigns.studio_offer.value")}>
                      {numberInput(coupon.value, (v) => patch({ value: v ?? 0 }), coupon.reward === "percentage" ? "%" : "CHF", coupon.reward === "percentage" ? 1 : 0.5)}
                    </Field>
                  )}
                  <Field label={t("campaigns.studio_offer.min_spend")} hint={t("campaigns.studio_offer.optional")}>
                    {numberInput(coupon.min_spend, (v) => patch({ min_spend: v }), "CHF", 0.5)}
                  </Field>
                  <Field label={t("campaigns.studio_offer.expires_in")} hint={t("campaigns.studio_offer.expires_hint")}>
                    {numberInput(coupon.expires_in_days, (v) => patch({ expires_in_days: v }), t("campaigns.studio_offer.days"))}
                  </Field>
                </div>

                <label class="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    class="toggle toggle-primary toggle-sm"
                    checked={coupon.single_use}
                    onChange={(e) => patch({ single_use: (e.target as HTMLInputElement).checked })}
                  />
                  <span class="label-text">{t("campaigns.studio_offer.single_use")}</span>
                </label>

                <div class="flex gap-3 rounded-xl bg-base-200 p-3 text-sm text-base-content/70">
                  <span class="material-symbols-outlined">qr_code_scanner</span>
                  {t("campaigns.studio_offer.redemption_explainer")}
                </div>
              </div>
            ) : (
              <div class="rounded-xl bg-base-200 p-4 text-sm text-base-content/70">{t("campaigns.studio_offer.message_only_hint")}</div>
            )}
          </div>

          <aside class="space-y-2">
            <div class="text-sm font-medium text-base-content/60">{t("campaigns.studio_offer.preview")}</div>
            <div class="rounded-2xl bg-base-100 p-5 shadow">
              <CouponCard
                offer={offer}
                code={sampleCouponCode("c1")}
                expiry={coupon?.expires_in_days ? formatShortDate(new Date(Date.now() + coupon.expires_in_days * 86_400_000)) : ""}
                headline={t("campaigns.studio_offer.preview_headline")}
                accent={props.campaign.email.theme.brand_color}
              />
            </div>
          </aside>
        </div>
      );
    };
  },
});
